import React, { Fragment, ReactNode } from "react";
import { TFunction } from "i18next";
import moment from "moment";
import numbro from "numbro";

// MUI Components
import Icon from "@mui/material/Icon";
import IconButton from '@mui/material/IconButton';
import Tooltip from "@mui/material/Tooltip";

// MUI Icons
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

// Custom Components
import { fieldFormat } from "@/lib/fieldFormat";
import { formats } from "@/lib/formats";
import { helpers } from "@/lib/helpers";
import {
  BoolFn,
  VoidFn,
  RenderFn,
  ISubModelsWithData,
  RowActionFn,
  AllowRowActionFn
} from "@/@types/ui/Table";
import { FieldAny } from "@/@types/models/model";
import SingleImageViewer from "@/ui/SingleImageViewer";

import Avenza from "@/images/avenza.png";
import Locus from "@/images/locus.png";
import { ShowDialogFn } from "@/@types/ui/DialogContext";
import { Box } from "@mui/material";

const zeroPad = (num: number, places: number) => String(num).padStart(places, '0')

const display_m_to_km = (value: number) => `${Math.round(value / 100) / 10} km`

const display_m = (value: number) => `${Math.round(value / 10) * 10} m`;

const display_hh_mm = (value: number) => `${ Math.floor(value / 60)} h : ${zeroPad(value % 60, 2)} min`

class tableGenerator {
  t: TFunction;
  onAction: RowActionFn;
  allowAction: AllowRowActionFn;
  constructor(t: TFunction, rowActionHandler: RowActionFn, allowRowAction: AllowRowActionFn) {
    this.t = t;
    const emptyFnc: VoidFn = (action, id) => { };
    const alwaysAllow: BoolFn = (action, id) => (true);

    this.onAction = rowActionHandler || emptyFnc;
    this.allowAction = allowRowAction || alwaysAllow;
  }


  generateRenderFunction(field: FieldAny, dc: ISubModelsWithData, showDialog: ShowDialogFn): RenderFn {
    const fieldDc = dc[field.source];
    switch (field.type) {
      case "text":
      case "wholenum":
      case "multiline":
        return ({ value }) => {
          if (field.items) {
            let pos = field.items.values.indexOf(value);
            const lbl = field.items.labels[pos];
            return field.translate ? this.t(lbl) : lbl;
            // format not defined on these types for now
            // } else if (field.format) {
            //   return fieldFormat(value, field.format, field.translate ? this.t : (v) => v);
          } else {
            return field.translate ? this.t(value) : (value !== undefined ? value : null);
          }
        };
      case "date":
        return ({ value }) => (
          value ? moment.utc(value).local().format(formats.date) : ""
        );
      case "datetime":
        return ({ value }) => (
          value ? moment.utc(value).local().format(formats.datetime) : ""
        );
      case "currency":
        return ({ value }) => {
          if (value && typeof value === "object") {
            if (value._value !== undefined && value._value !== null) {
              const num = numbro(value._value).format({ thousandSeparated: true, mantissa: 2 });
              return (
                //@ts-ignore
                <div style={{ textAlign: "right" }} unformatedvalue={value._value} type="currency">
                  {num}
                </div>
              );
            } else {
              return value;
            }
          } else if (typeof value === "number") {
            const num = numbro(value).format({ thousandSeparated: true, mantissa: 2 });
            return (
              //@ts-ignore
              <div unformatedvalue={value} type="currency">
                {field.currency} {num}
              </div>
            );
          } else {
            return null;
          }
        }
      case "active":
        return ({ value }) => {
          if (field.items) {
            let pos = field.items.values.indexOf(value);
            return this.t(field.items.labels[pos]);
          } else {
            return value;
          }
        }
      case "boolean":
      case "radio":
        return ({ value }) => {
          if (fieldDc) {
            if (value === null || value === undefined) {
              return "";
            }
            const rec = fieldDc.records.find((x) => x.value === value)
            return rec ? this.t(rec.label) : "";
          } 
          
          if (field.items) {
            const ind = field.items.values.indexOf(value && value.hasOwnProperty("value") ? value.value : value);

            return field.items.icons ? (
              <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                <Icon 
                  sx={{
                    color: field.items.iconColors && field.items.iconColors[ind] ? field.items.iconColors[ind] : undefined,
                  }}
                  color={field.items.iconColors && field.items.iconColors[ind] ? undefined : 'primary'}
                  fontSize="small">{field.items.icons[ind]}</Icon>
                <div style={{ fontSize: "14px", marginLeft: "4px" }}>{this.t(field.items.labels[ind])}</div>
              </div>
            ) : this.t(field.items.labels[ind]);
          } 
          
          return value;
        }
      case "checkbox":
        return ({ value }) => {
          if (Number.isInteger(value)) {
            if (fieldDc) {
              const rec = fieldDc.records.find((x) => x.value == value);
              return rec ? this.t(rec.label) : "";
            }
            const { items } = field;
            if (!items) {
              return null
            }
            // extract powers of two from value and add according labels to the codes array
            const codes = [];
            let i = 0;
            while (value) {       // it will eventually decrease to 0
              if (value & 1) {    // if smallest value bit is 1
                const p = 1 << i; // Math.pow(2, i);
                codes.push(this.t(items.labels[items.values.indexOf(p)]));
              }
              ++i;
              value >>= 1;        // value = Math.floor(value / 2);
            }
            return codes.join(", ");
          } else if (Array.isArray(value)) {
            if (fieldDc) {
              return value.map((x) => {
                const rec = fieldDc.records.find((f) => f.value === x || f.value === x.value)
                return rec ? this.t(rec.label) : "";
              }).filter(a => a !== "").join(", ");
            } else {
              const { items } = field;
              if (items) {
                const labels = value.map((x) => {
                  if (items.labels.indexOf(x)) {
                    return this.t(items.labels[x]);
                  } else {
                    return null
                  }
                }).filter(x => x !== null);
                return labels.join(", ");
              } else {
                return value;
              }
            }
          } else {
            return value;
          }
        }
      case "picker":
        return ({ value }) => {
          if (fieldDc) {
            if (value === null || value === undefined) return "";
        
            if (Array.isArray(value) && field.multi) {
              const labels = value.map((val) => {
                const rec = fieldDc.records.find((x) => x.value === val);
                return rec ? this.t(rec.label) : "";
              });
              return labels.join(", ");
            }
            const rec = fieldDc.records.find(
              (x) =>
                x.value === value ||
                (typeof value === "object" && value !== null && "value" in value && value.value === x.value)
            );
            return rec ? this.t(rec.label) : "";
          }
          if (typeof value === "object" && value !== null && "value" in value) {
            return value.value;
          }
          if (field.items) {
            const ind = field.items.values.indexOf(value);
            if (ind === -1) return "";
        
            const valueLabel = this.t(field.items.labels[ind]);
            return field.items.icons ? (
              <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                <Icon 
                  sx={{
                    color: field.items.iconColors && field.items.iconColors[ind] ? field.items.iconColors[ind] : undefined,
                  }}
                  color={field.items.iconColors && field.items.iconColors[ind] ? undefined : 'primary'}
                  fontSize="small">{field.items.icons[ind]}</Icon>
                <div style={{ fontSize: "14px", marginLeft: "4px" }}>{valueLabel}</div>
              </div>
            ) : valueLabel;
          }
          return value ?? "";
        };
      case "array":
        return ({ value }) => {
          return (<>
            {
              Array.isArray(value) ?
                value.map((x: any) => this.t(x)).join(", ")
                : this.t(value)
            }
          </>)
        }
      case "button":
        return ({ value, row }) => {
          const idAttribute = field.idref ? field.idref : "id";
          const allow = this.allowAction(field.action, row.original[idAttribute] as number);
          const title = field.tooltip ? this.t(field.tooltip) : "";
          return allow ? (
            <Tooltip title={title ? title : ""}>
              <IconButton
                aria-label={field.action}
                size="small"
                disabled={!allow}
                onClick={(evt) => {
                  evt.stopPropagation();
                  this.onAction(field.action, row.original[idAttribute] as number);
                }}
              >
                {field.icon === "avenza" ? <img src={Avenza} height="24" /> :
                  field.icon === "locus" ? <img src={Locus} height="24" /> :
                    <Icon color="primary">{field.icon}</Icon>
                }
              </IconButton>
            </Tooltip>
          ) : null;
        }
      case "link":
        return ({ value, row }) => {
          if (value) {
            const { recordIdField, recordType } = field.link;
            const recordId = row.original.hasOwnProperty(recordIdField) ? row.original[recordIdField] as number : 0;
            return (
              <Fragment>
                {value}
                <IconButton
                  size="small"
                  onClick={(evt) => {
                    evt.stopPropagation();
                    this.onAction(recordType, recordId);
                  }}
                >
                  <Icon color="primary">link</Icon>
                </IconButton>
              </Fragment>
            );
          } else {
            return null;
          }
        }
      case "mail":
        return ({ value }) => {
          if (value) {
            return (
              <Fragment>
                <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                    "&:hover .email-icon": {
                      opacity: 1, // Show on hover
                    }
                  }}
                >
                  {value}
                  <IconButton
                    aria-label={"mail"}
                    size="small"
                    onClick={(evt) => {
                      evt.stopPropagation();
                      window.open("mailto:" + value, "_blank");
                    }}
                    sx={{
                      opacity: 0,
                      transition: "opacity 0.3s ease"
                    }}
                    className="email-icon"
                  >
                    <Icon color="primary">mail_outline</Icon>
                  </IconButton>
                </Box>
              </Fragment>
            );
          } else {
            return null;
          }
        }
      case "url":
        return ({ value }) => {
          if (value) {
            const valueStripped = value.length > 30 ? `${value.substring(0, 30)}...` : value;
            return (
              <Fragment>
                <IconButton
                  aria-label={"url"}
                  size="small"
                  onClick={(evt) => {
                    evt.stopPropagation();
                    window.open(value, "_blank");
                  }}
                >
                  <Icon color="primary">language</Icon>
                </IconButton>
                {/* {valueStripped} */}
              </Fragment>
            );
          } else {
            return null;
          }
        }
      case "images":
        return ({ value }) => {
          if (value && Array.isArray(value)) {    
            return value.map((v, i) => {
              return v ? <IconButton
                aria-label="image"
                size="small"
                key={'icon-' + i}
                onClick={() => showDialog(() => <SingleImageViewer image={v} path={field.type} level={2} />, {}, 2)}
              >
                <ImageOutlinedIcon color="primary" />
              </IconButton> : null
            });
          } else {
            return null;
          }
        }
      case "numeric":
        return ({ value }) => {
          if (value === undefined || value === null) {
            return null;
          } 
          if (field.display === "display_m_to_km") {
            return display_m_to_km(value);
          }
          if (field.display === "display_hh_mm") {
            return display_hh_mm(value);
          }
          if (field.display === "display_m") {
            return display_m(value);
          }
          return String(value)
        }
      default:
        return ({ value }) => {
          if (value === undefined || value === null) {
            return null;
          } else {
            return String(value)
          };
        }
    }
  }
}

export default tableGenerator;
